import {
  datamuseQuery,
  definitionFor,
  PARTS_OF_SPEECH,
  type PartOfSpeech,
  splitEcho,
  type WordResult,
} from "@/lib/tool-kit/vocabulary/datamuse";

/**
 * The Synonym Finder's two questions and how their answers are merged.
 *
 * `rel_syn` returns WordNet synonyms: words in the same synset as the query, which is
 * the strict sense of "synonym". `ml` ("means like") casts wider — WordNet plus dozens
 * of dictionaries crawled by OneLook — and its definitions and parts of speech are
 * filtered to the sense that matched, which `rel_syn`'s are not. So `ml` supplies the
 * metadata wherever it has the word, `rel_syn` marks which words are true synonyms, and
 * the query echo (`qe=ml`) tells us the searched word's own parts of speech.
 */

const MAX_SYNONYMS = 60;
const MAX_RELATED = 100;

export function synonymQueries(word: string): [string, string] {
  return [
    datamuseQuery({ rel_syn: word, md: "dpf", max: MAX_SYNONYMS }),
    datamuseQuery({ ml: word, qe: "ml", md: "dpf", max: MAX_RELATED }),
  ];
}

export interface SynonymEntry {
  word: string;
  pos: PartOfSpeech;
  /** A WordNet synonym, rather than a word with a related meaning. */
  synonym: boolean;
  definition: string | null;
}

export interface SynonymGroup {
  pos: PartOfSpeech;
  entries: SynonymEntry[];
}

export interface SynonymResult {
  /** The searched word as Datamuse knows it: parts of speech and definitions. */
  echo: WordResult | null;
  groups: SynonymGroup[];
  total: number;
}

function entryFor(
  result: WordResult,
  pos: PartOfSpeech,
  synonym: boolean
): SynonymEntry {
  return {
    word: result.word,
    pos,
    synonym,
    definition: definitionFor(result, pos)?.text ?? null,
  };
}

/** Parts of speech in display order: the searched word's own first. */
function groupOrder(queryPos: readonly PartOfSpeech[]): PartOfSpeech[] {
  return [
    ...queryPos,
    ...PARTS_OF_SPEECH.filter((pos) => !queryPos.includes(pos)),
  ];
}

/**
 * Merge the two answers into part-of-speech groups.
 *
 * A word's group is its first part of speech in the `ml` answer, which is sense-specific.
 * A word only `rel_syn` returned is placed under the searched word's commonest part of
 * speech that it shares ("halcyon" is a noun and an adjective; as a synonym of "happy",
 * mostly an adjective, it is the adjective). Within a group, synonyms come first, each
 * set in Datamuse's order.
 */
export function groupSynonyms(
  word: string,
  strict: readonly WordResult[],
  related: readonly WordResult[]
): SynonymResult {
  const { echo, results } = splitEcho(related);
  const queryPos = echo?.pos.filter((pos) => pos !== "u") ?? [];
  const searched = word.toLowerCase();
  const strictWords = new Set(
    strict.map((result) => result.word.toLowerCase())
  );
  const entries = new Map<string, SynonymEntry>();

  for (const result of results) {
    const key = result.word.toLowerCase();
    if (key === searched || entries.has(key)) {
      continue;
    }
    const pos = result.pos[0] ?? "u";
    entries.set(
      key,
      entryFor(result, pos, result.synonym || strictWords.has(key))
    );
  }
  for (const result of strict) {
    const key = result.word.toLowerCase();
    if (key === searched || entries.has(key)) {
      continue;
    }
    const pos =
      queryPos.find((candidate) => result.pos.includes(candidate)) ??
      result.pos[0] ??
      "u";
    entries.set(key, entryFor(result, pos, true));
  }

  const all = [...entries.values()];
  const groups = groupOrder(queryPos)
    .map((pos) => {
      const members = all.filter((entry) => entry.pos === pos);
      return {
        pos,
        entries: [
          ...members.filter((entry) => entry.synonym),
          ...members.filter((entry) => !entry.synonym),
        ],
      };
    })
    .filter((group) => group.entries.length > 0);
  return { echo, groups, total: all.length };
}
